// Right panel — layer list + component library + canvas settings.
// The property-editing area was extracted into the floating PropsFloat panel (top-right of the canvas)
// instead of sitting under the layer list — with many layers the edit area got pushed off-screen (bad ergonomics).

import { useRef, useState } from 'react';
import { useT } from '../i18n';
import { ArrowUpIcon, ArrowDownIcon, XIcon, ImageObjIcon, TextObjIcon, RectObjIcon, FrameObjIcon, IconObjIcon, PlusIcon, ChevronRightIcon, EyeIcon, EyeOffIcon } from './icons';
import { toHex7 } from './PropsFloat';
import { flattenLayerTree, displayDropIndex } from '../lib/layerTree';
import type { CanvasObject, ComponentDef, JobRecord } from '../types';

export interface SidePanelProps {
  objects: CanvasObject[];
  selectedId: string | null;
  /** Multi-selection set */
  selectedIds: string[];
  jobs: Record<string, JobRecord>;
  onSelect: (id: string | null, additive?: boolean) => void;
  onReorder: (id: string, dir: 'up' | 'down') => void;
  /** Drag reorder — target index follows the objects array (0 = bottom-most) */
  onReorderTo: (id: string, toIndex: number) => void;
  onRemove: (id: string) => void;
  /** SPEC-405: per-layer show/hide toggle */
  onToggleVisible: (id: string) => void;
  /** SPEC-426 M2 C.2 (修复轮): layer-name double-click rename → dispatches RENAME_OBJECT */
  onRename: (id: string, name: string) => void;
  artboard: { w: number; h: number; bg: string };
  onCanvasResize: (w: number, h: number) => void;
  onCanvasBg: (color: string) => void;
  /** Component library */
  components: ComponentDef[];
  onSaveComponent: (name: string) => void;
  onInsertComponent: (id: string) => void;
  onDeleteComponent: (id: string) => void;
  /** Panel collapse/expand */
  collapsed: boolean;
  onToggleCollapse: () => void;
}

/** Artboard size presets (poster/mobile/desktop/square) */
export const ARTBOARD_PRESETS = [
  { key: 'poster', w: 1080, h: 1440 },
  { key: 'mobile', w: 390, h: 844 },
  { key: 'desktop', w: 1440, h: 900 },
  { key: 'square', w: 1080, h: 1080 },
] as const;

/** SPEC-405: group_id → stable hue (members of the same group always share the same color) */
function groupHue(gid: string): number {
  let h = 0;
  for (let i = 0; i < gid.length; i++) h = (h * 31 + gid.charCodeAt(i)) >>> 0;
  return h % 360;
}

export default function SidePanel({ objects, selectedId, selectedIds, jobs, onSelect, onReorder, onReorderTo, onRemove, onToggleVisible, onRename, artboard, onCanvasResize, onCanvasBg, components, onSaveComponent, onInsertComponent, onDeleteComponent, collapsed, onToggleCollapse }: SidePanelProps) {
  const t = useT();
  // SPEC-453 M1: tree-indented display order replaces the old flat reversed list — roots stay
  // top-most-first (old order preserved when nothing has a parent), children follow their frame,
  // collapsed frames hide their subtree. Collapse state lives here (default: all expanded).
  const [collapsedFrames, setCollapsedFrames] = useState<ReadonlySet<string>>(() => new Set());
  const rows = flattenLayerTree(objects, collapsedFrames);
  const toggleFrame = (id: string) => setCollapsedFrames(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Group member counts (SPEC-405): badge on each member row; clicking it selects the whole group
  const groupCounts = new Map<string, number>();
  for (const o of objects) if (o.group_id) groupCounts.set(o.group_id, (groupCounts.get(o.group_id) ?? 0) + 1);
  const [compName, setCompName] = useState('');
  // Inline delete confirmation (same pattern as ProjectSwitcher; text stays single-line via .project-confirm-text nowrap)
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  // Layer drag-reorder (HTML5 DnD). dropHint = insertion indicator (relative to display order).
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ id: string; pos: 'before' | 'after' } | null>(null);
  // SPEC-426 M2 C.2 (修复轮): inline layer rename (double-click on the layer name; Enter/blur
  // commits, Esc cancels — PageBar's page-rename pattern plus a settled-ref guard so a trailing
  // blur after Enter/Esc can never double-commit).
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameSettledRef = useRef(false);

  const startRename = (id: string, current: string) => {
    renameSettledRef.current = false;
    setRenamingId(id);
    setRenameDraft(current);
  };
  const commitRename = (id: string) => {
    if (renameSettledRef.current) return;
    renameSettledRef.current = true;
    setRenamingId(null);
    const name = renameDraft.trim();
    if (name) onRename(id, name); // empty ignored (the reducer guards too)
  };
  const cancelRename = () => {
    renameSettledRef.current = true; // a trailing blur must not commit after Esc
    setRenamingId(null);
  };

  const finishDrag = () => { setDragId(null); setDropHint(null); };

  /** Display-order slot → objects array index (SPEC-453 M1: mapping recomputed against the tree
   * display order via displayDropIndex — byte-identical to the old reversed-list formula when no
   * object has a parent; unit-tested in worker/test/spec453.test.mjs). */
  const commitDrop = (targetId: string, pos: 'before' | 'after') => {
    if (!dragId) { finishDrag(); return; }
    const toIndex = displayDropIndex(objects, rows, dragId, targetId, pos);
    if (toIndex == null) { finishDrag(); return; }
    onReorderTo(dragId, toIndex);
    finishDrag();
  };

  if (collapsed) {
    // SPEC-380: the expand button moved into the canvas topbar (pushes undo/redo aside) — the collapsed
    // panel itself is an empty zero-width strip.
    return <aside className="side-panel collapsed" />;
  }

  return (
    <aside className="side-panel">
      {/* The first section title uses the same panel-header spec as the left chat panel,
          so both divider lines align (same padding / same border-bottom) */}
      <div className="panel-header">
        {t('side.layers')}
        <button
          className="panel-collapse-btn"
          onClick={onToggleCollapse}
          title={t('panel.collapse.layers')}
          aria-label={t('panel.collapse.layers')}
        >
          <ChevronRightIcon />
        </button>
      </div>
      <div className="panel-section">
        {rows.length === 0 && <p className="hint">{t('side.layers.empty')}</p>}
        <ul
          className="layer-list"
          onDragOver={(e) => { if (dragId) e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); if (dragId) commitDrop(rows[rows.length - 1]?.obj.id ?? '', 'after'); }}
        >
          {rows.map(row => { const o = row.obj; return (
            <li
              key={o.id}
              style={{ paddingLeft: 8 + row.depth * 14 }}
              className={[
                'layer',
                o.id === selectedId ? 'selected' : '',
                o.visible === false ? 'is-hidden' : '',
                o.error ? 'has-error' : '',
                o.pending ? 'is-pending' : '',
                dragId === o.id ? 'dragging' : '',
                dropHint?.id === o.id ? `drop-${dropHint.pos}` : '',
              ].filter(Boolean).join(' ')}
              draggable
              onDragStart={(e) => { setDragId(o.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', o.id); } catch { /* ignore on legacy browsers */ } }}
              onDragEnd={finishDrag}
              onDragOver={(e) => {
                if (!dragId || dragId === o.id) return;
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setDropHint({ id: o.id, pos: e.clientY < r.top + r.height / 2 ? 'before' : 'after' });
              }}
              onDrop={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (dropHint && dropHint.id === o.id) commitDrop(o.id, dropHint.pos);
              }}
              onClick={(e) => onSelect(o.id, e.shiftKey)}
            >
              {/* SPEC-453 M1: frame collapse chevron (rotated = expanded); frames without children carry none */}
              {row.isFrameParent && (
                <button
                  className={`layer-chevron${row.collapsed ? '' : ' expanded'}`}
                  onClick={(e) => { e.stopPropagation(); toggleFrame(o.id); }}
                  title={row.collapsed ? t('side.layer.expand') : t('side.layer.collapse')}
                  aria-label={row.collapsed ? t('side.layer.expand') : t('side.layer.collapse')}
                  aria-expanded={!row.collapsed}
                >
                  <ChevronRightIcon />
                </button>
              )}
              {confirmRemoveId === o.id ? (
                <span className="layer-confirm" onClick={e => e.stopPropagation()}>
                  <span className="project-confirm-text">{t('project.deleteConfirm')}</span>
                  <button className="danger-btn" onClick={() => { onRemove(o.id); setConfirmRemoveId(null); }}>{t('project.delete')}</button>
                  <button className="ghost-btn" onClick={() => setConfirmRemoveId(null)}>{t('project.cancel')}</button>
                </span>
              ) : (
                <>
                  {o.group_id && (
                    <span
                      className="layer-group-badge"
                      style={{ background: `hsl(${groupHue(o.group_id)} 70% 55%)` }}
                      title={`${t('side.grouped')} · ${t('side.group.count', { n: groupCounts.get(o.group_id) ?? 1 })}`}
                      aria-label={t('side.grouped')}
                      onClick={(e) => { e.stopPropagation(); onSelect(o.id); }}
                    >
                      {groupCounts.get(o.group_id) ?? 1}
                    </span>
                  )}
                  <span className="layer-icon" aria-hidden="true">{o.type === 'image' ? <ImageObjIcon /> : o.type === 'rect' ? <RectObjIcon /> : o.type === 'frame' ? <FrameObjIcon /> : o.type === 'icon' ? <IconObjIcon /> : <TextObjIcon />}</span>
                  {renamingId === o.id ? (
                    /* SPEC-426 M2 C.2: inline rename — Enter/blur commits, Esc cancels. */
                    <input
                      className="layer-name-input"
                      autoFocus
                      value={renameDraft}
                      aria-label={t('side.layer.renameInput')}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      onDoubleClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') commitRename(o.id);
                        else if (e.key === 'Escape') cancelRename();
                      }}
                      onBlur={() => commitRename(o.id)}
                    />
                  ) : (
                    <span
                      className="layer-name"
                      title={o.name ? `${o.name} · ${t('side.layer.renameHint')}` : t('side.layer.renameHint')}
                      onDoubleClick={(e) => { e.stopPropagation(); startRename(o.id, o.name?.trim() ?? ''); }}
                    >
                      {/* SPEC-426 M2 B.1: semantic layer name first; unnamed (legacy) objects fall
                          back to the previous type/text label — zero migration. */}
                      {o.name?.trim()
                        ? o.name.trim().slice(0, 24)
                        : o.type === 'text'
                          ? (o.text ?? '').slice(0, 14) || t('side.layer.text')
                          : o.type === 'rect' ? t('side.layer.rect')
                          : o.type === 'frame' ? t('side.layer.frame')
                          : o.type === 'icon' ? t('side.layer.icon') : t('side.layer.image')}
                    </span>
                  )}
                  {/* SPEC-405: eye toggle — always visible (not gated on hover) so hidden rows can be restored */}
                  <span className="layer-eye" onClick={e => e.stopPropagation()}>
                    <button
                      title={o.visible === false ? t('side.layer.show') : t('side.layer.hide')}
                      aria-label={o.visible === false ? t('side.layer.show') : t('side.layer.hide')}
                      onClick={() => onToggleVisible(o.id)}
                    >
                      {o.visible === false ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </span>
                  <span className="layer-actions" onClick={e => e.stopPropagation()}>
                    <button title={t('side.layer.up')} aria-label={t('side.layer.up')} onClick={() => onReorder(o.id, 'up')}><ArrowUpIcon /></button>
                    <button title={t('side.layer.down')} aria-label={t('side.layer.down')} onClick={() => onReorder(o.id, 'down')}><ArrowDownIcon /></button>
                    <button title={t('side.layer.delete')} aria-label={t('side.layer.delete')} onClick={() => setConfirmRemoveId(o.id)}><XIcon /></button>
                  </span>
                </>
              )}
            </li>
          ); })}
        </ul>
      </div>

      {/* Component library: global reuse (save ≥1 selected objects / list / insert / delete) */}
      <div className="panel-section">
        <h4 className="panel-title">{t('side.components')}</h4>
        <div className="component-save-row">
          <input
            className="text-input"
            type="text"
            maxLength={24}
            placeholder={t('side.components.name')}
            value={compName}
            onChange={e => setCompName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && selectedIds.length > 0) { onSaveComponent(compName); setCompName(''); } }}
          />
          <button
            className="ghost-btn"
            disabled={selectedIds.length === 0}
            title={t('side.components.save')}
            onClick={() => { onSaveComponent(compName); setCompName(''); }}
          >
            <PlusIcon /> {t('side.components.save')}
          </button>
        </div>
        {components.length === 0 && <p className="hint">{t('side.components.empty')}</p>}
        <ul className="component-list">
          {components.map(c => (
            <li key={c.id} className="component-item">
              <span className="component-name" title={c.name}>{c.name}</span>
              <span className="component-meta">{t('side.components.count', { n: c.objects.length })}</span>
              <span className="component-actions">
                <button title={t('side.components.insert')} aria-label={t('side.components.insert')} onClick={() => onInsertComponent(c.id)}><PlusIcon /></button>
                <button title={t('side.components.delete')} aria-label={t('side.components.delete')} onClick={() => onDeleteComponent(c.id)}><XIcon /></button>
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="panel-section">
        <h4 className="panel-title">{t('side.canvas')}</h4>
        <label className="field">{t('side.canvas.size')}
          <select
            className="text-input"
            value={ARTBOARD_PRESETS.find(p => p.w === artboard.w && p.h === artboard.h)?.key ?? 'custom'}
            onChange={e => {
              const p = ARTBOARD_PRESETS.find(x => x.key === e.target.value);
              if (p) onCanvasResize(p.w, p.h);
            }}
          >
            {ARTBOARD_PRESETS.map(p => (
              <option key={p.key} value={p.key}>{t(`preset.${p.key}`)} {p.w}×{p.h}</option>
            ))}
            <option value="custom">{t('side.canvas.custom')} {artboard.w}×{artboard.h}</option>
          </select>
        </label>
        <label className="field">{t('side.canvas.bg')}
          <input className="text-input color-input" type="color" value={toHex7(artboard.bg)} onChange={e => onCanvasBg(e.target.value)} />
        </label>
      </div>
    </aside>
  );
}
